import { mobile, tablet } from "components/templates/Breakpoints";
import { BetterButton } from "components/atoms/BetterButton";
import { ClientOnly } from "components/templates/ClientOnly";
import { useBreakpoints } from "hooks";
import { Link } from "@components/atoms";
import { FC, PropsWithChildren, ReactElement } from "react";
import styled from "styled-components";

interface CallOutAdProps {
  title?: string;
  subtitle?: string | ReactElement;
  buttonText?: string;
  buttonLink?: string;
  hasButton?: boolean;
  backgroundColor?: string;
  imageLink?: string;
  textColor?: string;
  borderColor?: string;
  margin?: string;
  padding?: string;
  disclaimer?: string;
  backgroundPadding?: string;
  titleColor?: string;
  wrapBackgroundColour?: string;
}

export const CallOutAd: FC<PropsWithChildren<CallOutAdProps>> = (props) => (
  <ClientOnly style={{ width: "100%" }}>
    <_CallOutAd {...props} />
  </ClientOnly>
);

const _CallOutAd: FC<PropsWithChildren<CallOutAdProps>> = ({
  title,
  subtitle,
  buttonText,
  buttonLink,
  backgroundColor,
  textColor,
  children,
  borderColor,
  margin,
  padding,
  backgroundPadding,
  hasButton = buttonLink && buttonText,
  disclaimer,
  titleColor,
  wrapBackgroundColour,
}) => {
  const { isMobile } = useBreakpoints();

  return (
    <Wrap $padding={padding} margin={margin} $wrapBackgroundColour={wrapBackgroundColour}>
      <Background
        borderColor={borderColor}
        backgroundColor={backgroundColor}
        backgroundPadding={backgroundPadding}
      >
        <TextBlock textColor={textColor}>
          <Title titleColor={titleColor}>{title}</Title>
          <Subtitle $hasButton={hasButton}>{subtitle}</Subtitle>
          {hasButton && (
            <Link href={buttonLink} legacyBehavior>
              <a target="_blank" rel="noopener noreferrer">
                <BetterButton
                  variant="primary"
                  text={buttonText}
                  width={isMobile ? "100%" : "fit-content"}
                />
              </a>
            </Link>
          )}
        </TextBlock>
        {children}
      </Background>
      {disclaimer && <DisclaimerText>{disclaimer}</DisclaimerText>}
    </Wrap>
  );
};

export const Wrap = styled.div<{
  $padding?: string;
  margin?: string;
  $wrapBackgroundColour?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: ${(p) => p.margin || "7.5rem"};
  padding: ${(p) => p.$padding || 0};
  background: ${(p) => p.$wrapBackgroundColour};

  @media only screen and (max-width: 50rem) {
    margin: 0;
    padding: 2rem 3rem;
  }
`;

const Background = styled.div<{
  backgroundColor: string;
  borderColor: string;
  backgroundPadding: string;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${(p) => p.backgroundPadding};
  max-width: 58rem;
  background: ${(p) => p.backgroundColor};
  border-radius: 0.5rem;
  border: ${(p) => (p.borderColor ? `${p.borderColor} 1px solid` : null)};

  ${tablet} {
    flex-direction: column;
    width: 48rem;
    height: auto;
  }

  @media only screen and (max-width: 50rem) {
    justify-content: center;
    width: 100%;
  }
`;

const TextBlock = styled.div<{ textColor: string }>`
  display: flex;
  width: 31.5rem;
  flex-direction: column;
  align-items: left;
  margin: 2.5rem 0 2.5rem 2.5rem;

  h1,
  p {
    color: ${(p) => (p.textColor ? p.textColor : null)};
  }

  ${tablet} {
    width: 100%;
    padding: 0 2rem;
    margin: 2.5rem;
  }

  ${mobile} {
    margin-right: 2.5rem;
  }
`;

const Title = styled.h2<{ titleColor: string }>`
  letter-spacing: normal;
  font-size: 2.9rem;
  margin: 0;
  max-width: 30rem;
  color: ${(p) => (p.titleColor ? p.titleColor : null)};

  ${tablet} {
    max-width: 100%;
  }

  ${mobile} {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p<{ $hasButton: string | boolean }>`
  font-size: 1.25rem;
  letter-spacing: 0.075rem;
  margin: 0.938rem 0 2rem;
  margin-bottom: ${(p) => (p.$hasButton ? `2rem` : 0)};

  @media only screen and (max-width: 50rem) {
    width: auto;
  }

  ${mobile} {
    font-size: 1.1rem;
  }
`;

const DisclaimerText = styled.p`
  max-width: 50rem;
  padding-top: 2.5rem;
  text-align: center;
  font-size: 1rem;
  line-height: 1.5rem;
  letter-spacing: 0.075rem;
`;
